/*eslint-disable @typescript-eslint/member-ordering */

import { FilterTemplate, MultiSelectFilter } from '../filter-types';
import { LookupItem, StringLookupItem } from '@models/lookup';
import { of } from 'rxjs';
import moment from 'moment';
import { Moment } from 'moment';
import { cascadeToFilter } from '@shared/helpers/functions/filter-helpers';
import { AdvertiserInsightsService } from '@services/advertiser-insights/advertiser-insights.service';
import { switchMap } from 'rxjs/operators';
import { endDateFilter, startDateFilter } from '@models/base_filters';
import { UiConfig } from '@models/config';

const SUNDAY = 0;
const MONDAY = 1;
const NEXT_SUNDAY = 7;

export class AdvertiserInsightsOrdersFilters implements FilterTemplate {
    constructor(
        private advertiserInsightsSvc: AdvertiserInsightsService,
        public config: UiConfig,
        private orders: StringLookupItem[],
        private channels: number[],
        private savedFilters?: Record<string, unknown>,
        private defaultStartDate?: string,
        private defaultEndDate?: string,
    ) {}

    flightStart = startDateFilter()
        .SetKey('Flight Start')
        .Filter((date: Moment) => date?.day() === MONDAY)
        .Default(
            this.savedFilters?.flightStart
                ? moment(this.savedFilters.flightStart as string)
                : this.defaultStartDate
                  ? moment(this.defaultStartDate)
                  : null,
        );

    flightEnd = endDateFilter()
        .SetKey('Flight End')
        .ResetDate((date: Moment) => {
            if (date && this.flightStart.Value && date < moment(this.flightStart.Value)) {
                date.date(moment(this.flightStart.Value).date());
                date.month(moment(this.flightStart.Value).month());
                date.year(moment(this.flightStart.Value).year());
                date.day(NEXT_SUNDAY);
            }
            return date;
        })
        .Filter((date: Moment) => {
            if (date < moment(this.flightStart.Value)) {
                return false;
            }
            return date?.day() === SUNDAY;
        })
        .Default(
            this.savedFilters?.flightEnd
                ? moment(this.savedFilters.flightEnd as string)
                : this.defaultEndDate
                  ? moment(this.defaultEndDate)
                  : null,
        );

    categories = new MultiSelectFilter<StringLookupItem>('Categories')
        .Defer(() => of([]))
        .Transform((categories) => categories.map((category) => category.id))
        .Icon('fas fa-box');

    // salesRegions = new MultiSelectFilter<LookupItem>('Sales Region(s)')
    //     .Icon('fas fa-map-marked-alt');
    demographics = new MultiSelectFilter<LookupItem>('Demographic(s)')
        .Defer(() =>
            this.advertiserInsightsSvc
                .getOrderDemographics(
                    this.orders.map((item) => item.id),
                    this.channels,
                )
                .pipe(
                    switchMap((items: LookupItem[]) => {
                        this.demographics.Toggle(items.length === 0);
                        return of(items);
                    }),
                ),
        )
        .OnLoad((items: LookupItem[]) => {
            const savedDemos = this.savedFilters?.demographics as number[] | undefined;
            const selectedDemos = savedDemos?.length ? items.filter((item) => savedDemos.includes(item.id)) : items;
            this.demographics.Value = selectedDemos;

            const savedCategories = this.savedFilters?.categories as string[] | undefined;
            if (savedCategories?.length) {
                this.categories.isLoading = true;
                this.advertiserInsightsSvc
                    .getOrderCategories(
                        this.orders.map((item) => item.id),
                        this.channels,
                        selectedDemos.map((item) => item.id),
                    )
                    .subscribe(
                        (categories: StringLookupItem[]) => {
                            cascadeToFilter(this.categories, categories);
                            this.categories.Value = (this.categories.options as StringLookupItem[]).filter((c) =>
                                savedCategories.includes(c.id),
                            );
                        },
                        (err) => {
                            console.error(err);
                            this.categories.isLoading = false;
                            this.categories.loadError = 'Failed to Load';
                        },
                    );
            }
        })
        .OnChange((items: LookupItem[]) => {
            if (!this.demographics.isOpened) {
                this.categories.isLoading = true;
                this.advertiserInsightsSvc
                    .getOrderCategories(
                        this.orders.map((item) => item.id),
                        this.channels,
                        items.map((item) => item.id),
                    )
                    .subscribe(
                        (categories: StringLookupItem[]) => {
                            cascadeToFilter(this.categories, categories);
                        },
                        (err) => {
                            console.error(err);
                            this.categories.isLoading = false;
                            this.categories.loadError = 'Failed to Load';
                        },
                    );
            }
        })
        .Transform((demographics) => demographics.map((demographic) => demographic.id))
        .Toggle(true)
        .Icon('fas fa-id-card');

    accountExecutives = new MultiSelectFilter<LookupItem>((this.config?.customerConfig?.account_executive || 'AE') + '(s)')
        .Defer(() => this.advertiserInsightsSvc.getAccountExecutives(this.orders.map((item) => item.id)))
        .OnLoad((items: LookupItem[]) => {
            if (!this.accountExecutives.hide) {
                const savedAEs = this.savedFilters?.accountExecutives as number[] | undefined;
                this.accountExecutives.Value = savedAEs?.length ? items.filter((item) => savedAEs.includes(item.id)) : items;
            }
        })
        .Toggle(!this.config?.roleCapability.rate_optics?.advertiser_insights?.filter_by_ae)
        .Transform((aes) => aes.map((ae) => ae.id))
        .Icon('fas fa-users');
}
